import * as Sentry from "@sentry/react";
import { ReactNode } from "react";

type SentryErrorBoundaryProps = {
  children: ReactNode;
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;

  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }

  return "An unexpected application error occurred.";
};

const SentryFallback = ({ error }: { error: unknown }) => {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-semibold text-gray-900">
          Something went wrong.
        </h1>
        <p className="mt-3 text-sm text-gray-600">
          Please refresh the page or try again in a moment.
        </p>
        <p className="mt-3 break-words text-sm text-red-700" role="alert">
          Error: {getErrorMessage(error)}
        </p>
      </div>
    </div>
  );
};

const SentryErrorBoundary = ({ children }: SentryErrorBoundaryProps) => {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => <SentryFallback error={error} />}
      beforeCapture={(scope) => {
        scope.setTag("source", "react-error-boundary");
        scope.setExtra("route", window.location.pathname);
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default SentryErrorBoundary;
